/**
 * App Component
 * Root component managing global state and component coordination.
 */

import { useEffect, useReducer, useCallback, useState } from 'react';
import { Map } from './Map';
import { Controls } from './Controls';
import { AddressInput } from './AddressInput';
import { ErrorDisplay } from './ErrorDisplay';
import { LanguageButton } from './LanguageButton';
import { GlobeBackground } from './GlobeBackground';
import { AuthProvider, EmailGate, UsageIndicator, DisclaimerScreen, IntroScreen } from './index';
import { ErrorBoundary } from 'react-error-boundary';
import { useAnimationController } from '../hooks/useAnimationController';
import { useAuth, useUsage } from '../hooks';
import { TranslationProvider, useTranslation } from '../hooks/useTranslation.tsx';
import type { AppState, Coordinates, TravelMode, MapLanguage } from '../types';
import { decodeState, updateURL, createShareableURL, getCurrentQueryString } from '../utils/urlStateManager';
import { checkBrowserCompatibility, getBrowserErrorMessage } from '../utils/browserCheck';
import { getBrowserLanguage } from '../utils/i18n';
import { trackPageLoad } from '../services/analyticsService';
import type { Map as MapboxMap } from 'mapbox-gl';
import './App.css';

/**
 * Action types for state reducer
 */
type AppAction =
  | { type: 'SET_ORIGIN'; payload: Coordinates | null }
  | { type: 'SET_MAX_TIME'; payload: number }
  | { type: 'SET_TRAVEL_MODE'; payload: TravelMode }
  | { type: 'SET_ANIMATION_SPEED'; payload: number }
  | { type: 'SET_CURRENT_TIME'; payload: number }
  | { type: 'SET_ANIMATION_STATE'; payload: AppState['animationState'] }
  | { type: 'SET_MAP_STYLE'; payload: 'streets' | 'satellite' }
  | { type: 'LOAD_STATE'; payload: Partial<AppState> };

/**
 * Initial application state
 */
const initialState: AppState = {
  origin: null,
  maxTime: 60,
  currentTime: 0,
  travelMode: 'driving',
  animationState: 'idle',
  animationSpeed: 1,
};

/**
 * Application state reducer
 */
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_ORIGIN':
      return { ...state, origin: action.payload };
    case 'SET_MAX_TIME':
      return { ...state, maxTime: action.payload };
    case 'SET_TRAVEL_MODE':
      return { ...state, travelMode: action.payload };
    case 'SET_ANIMATION_SPEED':
      return { ...state, animationSpeed: action.payload };
    case 'SET_CURRENT_TIME':
      return { ...state, currentTime: action.payload };
    case 'SET_ANIMATION_STATE':
      return { ...state, animationState: action.payload };
    case 'SET_MAP_STYLE':
      // This action updates local state, not part of AppState
      return state;
    case 'LOAD_STATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

/**
 * App Component
 * Manages global state and coordinates Map and Controls components.
 */
/**
 * Error Fallback Component
 */
function ErrorFallback({ error: _error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="error-boundary">
      <ErrorDisplay
        message={t.error.somethingWentWrong}
        onRetry={resetErrorBoundary}
      />
    </div>
  );
}

const App: React.FC = () => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [mapInstance, setMapInstance] = useState<MapboxMap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [browserCompatible, setBrowserCompatible] = useState<boolean | null>(
    null
  );
  const [mapStyle, setMapStyle] = useState<'streets' | 'satellite'>('streets');
  const [language, setLanguage] = useState<MapLanguage>(() => {
    // Try to get from URL or localStorage, otherwise use browser language
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('language') as MapLanguage | null;
    if (urlLang && ['en', 'zh-Hans', 'zh-Hant', 'es', 'fr', 'de', 'ja', 'ko'].includes(urlLang)) {
      return urlLang;
    }
    
    const stored = localStorage.getItem('language') as MapLanguage | null;
    if (stored && ['en', 'zh-Hans', 'zh-Hant', 'es', 'fr', 'de', 'ja', 'ko'].includes(stored)) {
      return stored;
    }
    
    return getBrowserLanguage();
  });

  const animationController = useAnimationController();

  // Check browser compatibility on mount
  useEffect(() => {
    const compatibility = checkBrowserCompatibility();
    setBrowserCompatible(compatibility.isCompatible);
    if (!compatibility.isCompatible) {
      const errorMsg = getBrowserErrorMessage(compatibility);
      if (errorMsg) {
        setError(errorMsg);
      }
    }
  }, []);

  // Track page load for analytics
  useEffect(() => {
    trackPageLoad();
    
    // Initialize PostHog analytics
    (async () => {
      const { initPostHog } = await import('../services/posthogService');
      initPostHog();
    })();
  }, []);

  // Handle errors from animation controller
  useEffect(() => {
    if (animationController.error) {
      // Note: Translation happens in AppContent where t is available
      setError(animationController.error);
    }
  }, [animationController.error]);

  // Set map instance in animation controller
  useEffect(() => {
    animationController.setMapInstance(mapInstance);
  }, [mapInstance, animationController]);

  // Load state from URL on mount - only load language if not authenticated
  useEffect(() => {
    const queryString = getCurrentQueryString();
    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      
      // Always load language from URL if present
      const urlLang = urlParams.get('language') as MapLanguage | null;
      if (urlLang && ['en', 'zh-Hans', 'zh-Hant', 'es', 'fr', 'de', 'ja', 'ko'].includes(urlLang)) {
        setLanguage(urlLang);
      }
      
      // Only decode full state if we have other parameters (will be used when authenticated)
      const decodedState = decodeState(queryString);
      if (decodedState) {
        dispatch({ type: 'LOAD_STATE', payload: decodedState });

        // Load extended state (map style)
        if (decodedState.mapStyle) {
          setMapStyle(decodedState.mapStyle);
        }

        // Auto-play if requested (userId will be passed from AppContent if authenticated)
        // Note: Auto-play is handled in AppContent after AuthProvider wraps
      }
    }
  }, []);

  // Save language to localStorage
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Sync animation controller state with app state
  useEffect(() => {
    dispatch({ type: 'SET_CURRENT_TIME', payload: animationController.currentTime });
    dispatch({ type: 'SET_ANIMATION_STATE', payload: animationController.animationState });
  }, [animationController.currentTime, animationController.animationState]);

  // Handle location click from map
  const handleLocationClick = useCallback((coords: Coordinates) => {
    dispatch({ type: 'SET_ORIGIN', payload: coords });
  }, []);

  // Handle address selection
  const handleAddressSelect = useCallback((coords: Coordinates) => {
    dispatch({ type: 'SET_ORIGIN', payload: coords });
  }, []);

  // Handle map style change
  const handleMapStyleChange = useCallback((style: 'streets' | 'satellite') => {
    setMapStyle(style);
  }, []);

  // Handle language change
  const handleLanguageChange = useCallback((lang: MapLanguage) => {
    setLanguage(lang);
  }, []);

  // Handle map ready
  const handleMapReady = useCallback((map: MapboxMap) => {
    setMapInstance(map);
  }, []);

  // Handle time change
  const handleTimeChange = useCallback((maxTime: number) => {
    dispatch({ type: 'SET_MAX_TIME', payload: maxTime });
  }, []);

  // Handle mode change
  const handleModeChange = useCallback((mode: TravelMode) => {
    dispatch({ type: 'SET_TRAVEL_MODE', payload: mode });
  }, []);

  // Handle speed change
  const handleSpeedChange = useCallback((speed: number) => {
    dispatch({ type: 'SET_ANIMATION_SPEED', payload: speed });
  }, []);


  // Note: handleErrorRetry moved to AppContent where it has access to user

  // Handle error dismiss
  const handleErrorDismiss = useCallback(() => {
    setError(null);
  }, []);

  // Show browser compatibility error if incompatible
  if (browserCompatible === false) {
    return (
      <AuthProvider>
        <TranslationProvider language={language}>
          <div className="app">
            <div className="browser-error">
              <BrowserErrorDisplay error={error} />
            </div>
          </div>
        </TranslationProvider>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <TranslationProvider language={language}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <AuthGatedApp
            state={state}
            mapInstance={mapInstance}
            error={error}
            mapStyle={mapStyle}
            language={language}
            animationController={animationController}
            onLocationClick={handleLocationClick}
            onMapReady={handleMapReady}
            onAddressSelect={handleAddressSelect}
            onTimeChange={handleTimeChange}
            onModeChange={handleModeChange}
            onSpeedChange={handleSpeedChange}
          onMapStyleChange={handleMapStyleChange}
          onLanguageChange={handleLanguageChange}
          onErrorDismiss={handleErrorDismiss}
          />
        </ErrorBoundary>
      </TranslationProvider>
    </AuthProvider>
  );
};

/**
 * Browser Error Display Component
 * Uses translations for browser compatibility error
 */
function BrowserErrorDisplay({ error }: { error: string | null }) {
  const { t } = useTranslation();
  return (
    <ErrorDisplay
      message={error || t.error.browserNotSupported}
    />
  );
}

/**
 * AuthGatedApp Component
 * Shows EmailGate if not authenticated, otherwise shows app content
 */
interface AuthGatedAppProps {
  state: AppState;
  mapInstance: MapboxMap | null;
  error: string | null;
  mapStyle: 'streets' | 'satellite';
  language: MapLanguage;
  animationController: ReturnType<typeof useAnimationController>;
  onLocationClick: (coords: Coordinates) => void;
  onMapReady: (map: MapboxMap) => void;
  onAddressSelect: (coords: Coordinates) => void;
  onTimeChange: (maxTime: number) => void;
  onModeChange: (mode: TravelMode) => void;
  onSpeedChange: (speed: number) => void;
  onMapStyleChange: (style: 'streets' | 'satellite') => void;
  onLanguageChange: (lang: MapLanguage) => void;
  onErrorDismiss: () => void;
}

const AuthGatedApp: React.FC<AuthGatedAppProps> = (props) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [hasSeenDisclaimer, setHasSeenDisclaimer] = useState(false);
  const [hasSeenIntro, setHasSeenIntro] = useState(() => {
    // Check localStorage to see if user has already seen the intro
    if (typeof window !== 'undefined') {
      return localStorage.getItem('hasSeenIntro') === 'true';
    }
    return false;
  });

  // Clear URL parameters when showing EmailGate (only keep language)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Clear all URL parameters except language
      const urlParams = new URLSearchParams(window.location.search);
      const language = urlParams.get('language');
      const newURL = language 
        ? `${window.location.pathname}?language=${language}`
        : window.location.pathname;
      window.history.replaceState({}, '', newURL);
    }
  }, [authLoading, isAuthenticated]);

  // Reset disclaimer and intro when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setHasSeenDisclaimer(false);
      // Don't reset hasSeenIntro - keep it persistent across sessions
    }
  }, [isAuthenticated]);

  // Handle intro completion
  const handleIntroComplete = useCallback(() => {
    setHasSeenIntro(true);
    localStorage.setItem('hasSeenIntro', 'true');
  }, []);

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="app">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  // Show EmailGate, DisclaimerScreen, and IntroScreen on globe background, or show main app
  if (!isAuthenticated || !hasSeenDisclaimer || !hasSeenIntro) {
    return (
      <>
        {/* Show globe background for EmailGate and DisclaimerScreen only */}
        {(!isAuthenticated || !hasSeenDisclaimer) && (
          <GlobeBackground language={props.language} />
        )}
        
        {/* Show EmailGate if not authenticated */}
        {!isAuthenticated ? (
          <EmailGate 
            language={props.language} 
            onLanguageChange={props.onLanguageChange}
          />
        ) : !hasSeenDisclaimer ? (
          /* Show Disclaimer after authentication */
          <DisclaimerScreen
            language={props.language}
            onComplete={() => setHasSeenDisclaimer(true)}
          />
        ) : !hasSeenIntro ? (
          /* Show Intro tour after disclaimer (on top of actual app) */
          <>
            <AppContent {...props} showTourButton={false} />
            <IntroScreen onComplete={handleIntroComplete} />
          </>
        ) : null}
      </>
    );
  }

  // Show app content if authenticated, has seen disclaimer, and has seen intro
  // Also show "Take Tour" button for returning users
  const handleShowIntroAgain = () => {
    setHasSeenIntro(false);
  };

  return <AppContent {...props} showTourButton={true} onShowIntro={handleShowIntroAgain} />;
};

/**
 * App Content Component
 * Separated to use translation hook
 */
interface AppContentProps {
  state: AppState;
  mapInstance: MapboxMap | null;
  error: string | null;
  mapStyle: 'streets' | 'satellite';
  language: MapLanguage;
  animationController: ReturnType<typeof useAnimationController>;
  onLocationClick: (coords: Coordinates) => void;
  onMapReady: (map: MapboxMap) => void;
  onAddressSelect: (coords: Coordinates) => void;
  onTimeChange: (maxTime: number) => void;
  onModeChange: (mode: TravelMode) => void;
  onSpeedChange: (speed: number) => void;
  onMapStyleChange: (style: 'streets' | 'satellite') => void;
  onLanguageChange: (lang: MapLanguage) => void;
  onErrorDismiss: () => void;
  onShowIntro?: () => void;
  showTourButton?: boolean;
}

const AppContent: React.FC<AppContentProps> = ({
  state,
  error,
  mapStyle,
  language,
  animationController,
  onLocationClick,
  onMapReady,
  onAddressSelect,
  onTimeChange,
  onModeChange,
  onSpeedChange,
  onMapStyleChange,
  onLanguageChange,
  onErrorDismiss,
  onShowIntro,
  showTourButton = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { usageCount, usageLimit, isLoading: usageLoading } = useUsage(user?.id);


  // Translate error messages
  const getTranslatedError = (errorMsg: string | null): string | null => {
    if (!errorMsg) return null;
    if (errorMsg === 'USAGE_LIMIT_EXCEEDED') {
      return t.error.usageLimitReached;
    }
    // For other errors, return as-is (they're already translated or user-friendly)
    return errorMsg;
  };

  // Update URL when state changes (only when authenticated)
  useEffect(() => {
    updateURL({
      ...state,
      mapStyle,
      language,
    });
  }, [state.origin, state.maxTime, state.travelMode, state.animationSpeed, state.animationState, mapStyle, language]);

  // Handle error retry (moved here to access user)
  const handleErrorRetry = useCallback(() => {
    if (state.origin) {
      animationController
        .startAnimation(
          state.origin,
          state.maxTime,
          state.travelMode,
          state.animationSpeed,
          user?.id
        )
        .catch((err) => {
          // Error will be handled by animationController
          console.error('Animation retry failed:', err);
        });
    }
  }, [state, animationController, user?.id]);

  // Handle share with translations
  const handleShare = useCallback(() => {
    const shareableURL = createShareableURL({
      ...state,
      animationState: 'idle', // Reset to idle for sharing
      currentTime: 0,
      mapStyle,
      language,
    });

    // Copy to clipboard with fallback for browsers that don't support Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareableURL).then(
        () => {
          alert(t.share.urlCopied);
        },
        (err) => {
          console.error('Failed to copy URL:', err);
          // Fallback: show URL in alert
          alert(`${t.share.shareUrl}: ${shareableURL}`);
        }
      );
    } else {
      // Fallback for browsers without Clipboard API support
      // Try using the older execCommand method
      try {
        const textArea = document.createElement('textarea');
        textArea.value = shareableURL;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          alert(t.share.urlCopied);
        } else {
          // Last resort: show URL in alert
          alert(`${t.share.shareUrl}: ${shareableURL}`);
        }
      } catch (err) {
        console.error('Failed to copy URL:', err);
        // Last resort: show URL in alert
        alert(`${t.share.shareUrl}: ${shareableURL}`);
      }
    }
    }, [state, mapStyle, language, t]);

  return (
    <div className="app">
      <Map
        origin={state.origin}
        onLocationClick={onLocationClick}
        onMapReady={onMapReady}
        mapStyle={mapStyle}
        language={language}
      />
      <div className="app-top-bar">
        <div className="app-address-bar">
          <AddressInput
            origin={state.origin}
            onAddressSelect={onAddressSelect}
          />
        </div>
        <div className="app-top-bar-buttons">
          {showTourButton && onShowIntro && (
            <button
              type="button"
              onClick={onShowIntro}
              className="tour-button"
              aria-label={t.intro.startTour}
              title={t.intro.startTour}
            >
              ?
            </button>
          )}
          <LanguageButton
            language={language}
            onLanguageChange={onLanguageChange}
          />
        </div>
      </div>
      <UsageIndicator
        usageCount={usageCount}
        usageLimit={usageLimit}
        isLoading={usageLoading}
      />
      {error && (
        <div className="app-error">
          <ErrorDisplay
            message={getTranslatedError(error) || error}
            onRetry={error === 'USAGE_LIMIT_EXCEEDED' ? undefined : handleErrorRetry}
            dismissible
            onDismiss={onErrorDismiss}
          />
        </div>
      )}
      <Controls
        maxTime={state.maxTime}
        currentTime={state.currentTime}
        travelMode={state.travelMode}
        animationSpeed={state.animationSpeed}
        animationState={state.animationState}
        isLoading={animationController.isLoading}
        error={animationController.error}
        animationController={animationController}
        onTimeChange={onTimeChange}
        onModeChange={onModeChange}
        onSpeedChange={onSpeedChange}
        origin={state.origin}
        onShare={handleShare}
        mapStyle={mapStyle}
        onMapStyleChange={onMapStyleChange}
      />
      {animationController.animationState !== 'idle' && (
        <div className="app-progress-bar">
          <div className="progress-text">
            {Math.round(animationController.currentTime)} {t.progress.minutes} / {state.maxTime} {t.progress.minutes}
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{
                width: `${(animationController.currentTime / state.maxTime) * 100}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
